import { Construction, Home, Sparkles } from "lucide-react";
import Link from "next/link";
import { BackButton } from "./BackButton";
import { Typography } from "@/components/common/ui/Typography";

export interface UnderConstructionProps {
  title?: string;
  description?: string;
  showBackButton?: boolean;
}

export function UnderConstruction({
  title = "Coming Soon",
  description = "This feature is under construction",
  showBackButton = true,
}: UnderConstructionProps) {
  return (
    <div className="h-screen relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-100 flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-navigator-secondary/30 rounded-full blur-3xl animate-blob" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-navigator-accent/30 rounded-full blur-3xl animate-blob animation-delay-2000" />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-amber-200/20 rounded-full blur-3xl animate-blob animation-delay-4000" />
      </div>

      <div className="max-w-3xl w-full relative z-10">
        <div className="text-center mb-8">
          <div className="relative inline-block mb-6">
            <div className="absolute inset-0 bg-navigator-primary/30 rounded-full blur-2xl animate-pulse" />
            <div className="relative bg-white rounded-full p-6 shadow-2xl border-4 border-navigator-accent/20">
              <Construction
                className="w-16 h-16 text-navigator-primary"
                strokeWidth={1.5}
              />
            </div>
            <div className="absolute -top-2 -right-2">
              <Sparkles className="w-6 h-6 text-amber-400 animate-pulse" />
            </div>
          </div>

          <Typography variant="display2" color="text-navigator-primary-dark" className="md:text-5xl mb-3 tracking-tight">
            {title}
          </Typography>

          <Typography variant="headline3" weight="normal" color="muted" as="p" className="mb-3">
            {description}
          </Typography>

          <Typography variant="body" color="light" as="p" className="max-w-xl mx-auto leading-relaxed">
            We're crafting something amazing for you. In the meantime, explore
            other sections of Broker AI or return to your dashboard.
          </Typography>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
          <Link href="/home" className="w-full sm:w-auto">
            <div className="group relative overflow-hidden bg-navigator-primary hover:bg-navigator-primary-dark text-white rounded-2xl transition-all duration-300 shadow-lg hover:shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000" />
              <div className="relative flex items-center justify-center gap-3 px-8 py-4">
                <Home className="w-5 h-5" />
                <span className="font-semibold text-lg">Go to Dashboard</span>
              </div>
            </div>
          </Link>

          {showBackButton && <BackButton />}
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full shadow-md border border-gray-200">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            <Typography variant="body" color="muted" as="span">
              Need help? Contact our support team
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
}
